/**
 * PD-17336 — pure helpers behind the "Sign-in sessions" card on Settings,
 * Authentication, and crm-api's validation of what it saves.
 *
 * The card edits three limits for the CRM and for the ecommerce storefront:
 *
 * - idle sign-out (tenant setting `security.idle_timeout`, stored in minutes),
 * - "require signing in again every ..." (`dvp._instance._tokenExpiresIn` /
 *   `_tokenExpiresIn_public`, stored as a duration string), and
 * - "sign everyone out on a schedule" (`_tokenExpirationSchedule` /
 *   `_tokenExpirationSchedule_public`, stored as a cron expression).
 *
 * Lengths are typed as a number plus a unit (minutes, hours or days), so the
 * helpers here convert between that pair, minutes, and the stored formats.
 * Everything is free of DOM and React so crm-api can run the same rules.
 */

import {
	IDLE_TIMEOUT_MAX_MINUTES,
	IDLE_TIMEOUT_MIN_MINUTES,
	SIGN_IN_SESSION_COLUMN_MAX_LENGTH,
	SIGN_IN_SESSION_MAX_MINUTES,
	SIGN_IN_SESSION_MIN_MINUTES,
	type SignInSessionPolicyModel,
	type UpdateSignInSessionSettingsRequestDto,
} from '@supply-mover-2-0/shared';
import { parseDurationToSeconds } from '../utils/token-lifetime';

/** The units a length can be typed in. */
export type SessionLengthUnitModel = 'minutes' | 'hours' | 'days';

export const SESSION_LENGTH_UNITS: ReadonlyArray<SessionLengthUnitModel> = [
	'minutes',
	'hours',
	'days',
];

export const MINUTES_PER_SESSION_LENGTH_UNIT: Readonly<
	Record<SessionLengthUnitModel, number>
> = {
	minutes: 1,
	hours: 60,
	days: 24 * 60,
};

/** A length as the card shows it: a whole number and its unit. */
export interface SessionLengthModel {
	amount: number;
	unit: SessionLengthUnitModel;
}

/** Allowed range of a length, in minutes. */
export interface SessionLengthBoundsModel {
	minMinutes: number;
	maxMinutes: number;
}

/** Idle sign-out: 1 minute to 365 days (`security.idle_timeout`). */
export const IDLE_SIGN_OUT_LENGTH_BOUNDS: SessionLengthBoundsModel = {
	minMinutes: IDLE_TIMEOUT_MIN_MINUTES,
	maxMinutes: IDLE_TIMEOUT_MAX_MINUTES,
};

/** "Require signing in again every ...": 1 minute to 365 days. */
export const SIGN_IN_AGAIN_LENGTH_BOUNDS: SessionLengthBoundsModel = {
	minMinutes: SIGN_IN_SESSION_MIN_MINUTES,
	maxMinutes: SIGN_IN_SESSION_MAX_MINUTES,
};

/**
 * The largest unit that represents `minutes` exactly, so a saved 43200 shows
 * as 30 days and a saved 90 as 90 minutes (not 1.5 hours).
 */
export function toLargestExactUnit(minutes: number): SessionLengthModel {
	if (Number.isInteger(minutes) && minutes > 0) {
		for (const unit of ['days', 'hours'] as const) {
			const perUnit = MINUTES_PER_SESSION_LENGTH_UNIT[unit];
			if (minutes % perUnit === 0) {
				return { amount: minutes / perUnit, unit };
			}
		}
	}
	return { amount: minutes, unit: 'minutes' };
}

/** Result of reading one number box with its unit. */
export interface SessionLengthInputModel {
	/** Total minutes to save; null when blank (off) or invalid. */
	minutes: number | null;
	/** Why the text cannot be saved, or null when it can. */
	error: 'not_whole_number' | 'out_of_range' | null;
}

/**
 * Read a number box and its unit: blank is "off", otherwise a whole number
 * whose total minutes fall within `bounds`.
 */
export function parseSessionLengthInput(
	raw: string,
	unit: SessionLengthUnitModel,
	bounds: SessionLengthBoundsModel,
): SessionLengthInputModel {
	const trimmed = raw.trim();
	if (trimmed === '') {
		return { minutes: null, error: null };
	}
	if (!/^\d+$/.test(trimmed)) {
		return { minutes: null, error: 'not_whole_number' };
	}
	const minutes = Number(trimmed) * MINUTES_PER_SESSION_LENGTH_UNIT[unit];
	if (
		!Number.isSafeInteger(minutes) ||
		minutes < bounds.minMinutes ||
		minutes > bounds.maxMinutes
	) {
		return { minutes: null, error: 'out_of_range' };
	}
	return { minutes, error: null };
}

/** Duration-string suffix per unit; the grammar the login app and SM1 share. */
const TOKEN_DURATION_SUFFIX: Readonly<Record<SessionLengthUnitModel, string>> =
	{
		minutes: 'm',
		hours: 'h',
		days: 'd',
	};

/**
 * Minutes as a `_tokenExpiresIn` value in the largest exact unit: `30d`,
 * `12h`, `45m`. Always a whole number with a unit letter: the legacy SM1
 * server passes the value to jsonwebtoken, which reads a bare number as
 * milliseconds, while the login app reads it as seconds.
 */
export function minutesToTokenDuration(minutes: number): string {
	const { amount, unit } = toLargestExactUnit(minutes);
	return `${amount}${TOKEN_DURATION_SUFFIX[unit]}`;
}

/** A stored limit as the card can show it. */
export type StoredSessionLengthModel =
	| { kind: 'off' }
	| { kind: 'length'; minutes: number }
	/** Set some other way, in a form this screen cannot show as a number. */
	| { kind: 'custom'; value: string };

/**
 * Read a stored `_tokenExpiresIn` value. Anything the login app accepts
 * (`10d`, `2w`, `3600`, `2.5 hours`) that comes to whole minutes shows as a
 * length; anything else (seconds that are not whole minutes, or text the
 * login app ignores) is kept as custom rather than silently dropped.
 */
export function readTokenDuration(
	value: string | null | undefined,
): StoredSessionLengthModel {
	if (value == null || value.trim() === '') {
		return { kind: 'off' };
	}
	const seconds = parseDurationToSeconds(value);
	if (seconds == null || seconds % 60 !== 0) {
		return { kind: 'custom', value };
	}
	return { kind: 'length', minutes: seconds / 60 };
}

/** The only duration form the CRM writes: a whole number and m, h or d. */
const CANONICAL_TOKEN_DURATION = /^([1-9]\d*)([mhd])$/;

/**
 * Minutes in a duration the CRM may save, or null when it is not one: a
 * whole number with `m`, `h` or `d`, from 1 minute to 365 days.
 */
export function canonicalTokenDurationMinutes(value: string): number | null {
	const match = CANONICAL_TOKEN_DURATION.exec(value);
	if (match == null || match[1] == null || match[2] == null) {
		return null;
	}
	const unit: SessionLengthUnitModel =
		match[2] === 'd' ? 'days' : match[2] === 'h' ? 'hours' : 'minutes';
	const minutes = Number(match[1]) * MINUTES_PER_SESSION_LENGTH_UNIT[unit];
	if (
		!Number.isSafeInteger(minutes) ||
		minutes < SIGN_IN_AGAIN_LENGTH_BOUNDS.minMinutes ||
		minutes > SIGN_IN_AGAIN_LENGTH_BOUNDS.maxMinutes
	) {
		return null;
	}
	return minutes;
}

/** Day of the week as cron counts it: 0 is Sunday, 6 is Saturday. */
export type ScheduleDayOfWeekModel = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** A sign-out schedule as the card shows it. */
export type SignOutScheduleModel =
	| { kind: 'off' }
	| { kind: 'daily'; hour: number; minute: number }
	| {
			kind: 'weekly';
			dayOfWeek: ScheduleDayOfWeekModel;
			hour: number;
			minute: number;
	  }
	/** A cron expression that is not a daily or weekly time. */
	| { kind: 'custom'; expression: string };

/** The schedules the card can create. */
export type EditableSignOutScheduleModel = Exclude<
	SignOutScheduleModel,
	{ kind: 'custom' }
>;

/** A plain cron number (no ranges, steps or names) within bounds, or null. */
function parseCronNumber(field: string, max: number): number | null {
	if (!/^\d{1,2}$/.test(field)) {
		return null;
	}
	const value = Number(field);
	return value <= max ? value : null;
}

/**
 * Read a stored schedule. A daily time (`30 2 * * *`) or a weekly one
 * (`0 5 * * 1`) becomes the picker's value; any other expression, including
 * ranges, steps, day names or a seconds field, is custom.
 */
export function cronToSchedule(
	expression: string | null | undefined,
): SignOutScheduleModel {
	if (expression == null || expression.trim() === '') {
		return { kind: 'off' };
	}
	const trimmed = expression.trim();
	const fields = trimmed.split(/\s+/);
	if (fields.length !== 5) {
		return { kind: 'custom', expression: trimmed };
	}
	const [minuteField, hourField, dayOfMonth, month, dayOfWeekField] = fields;
	const minute = parseCronNumber(minuteField ?? '', 59);
	const hour = parseCronNumber(hourField ?? '', 23);
	if (minute == null || hour == null || dayOfMonth !== '*' || month !== '*') {
		return { kind: 'custom', expression: trimmed };
	}
	if (dayOfWeekField === '*') {
		return { kind: 'daily', hour, minute };
	}
	// Cron allows 7 as a second spelling of Sunday.
	const dayOfWeek = parseCronNumber(dayOfWeekField ?? '', 7);
	if (dayOfWeek == null) {
		return { kind: 'custom', expression: trimmed };
	}
	return {
		kind: 'weekly',
		dayOfWeek: (dayOfWeek % 7) as ScheduleDayOfWeekModel,
		hour,
		minute,
	};
}

/** The cron expression for a picker value, or null for off. */
export function scheduleToCron(
	schedule: EditableSignOutScheduleModel,
): string | null {
	switch (schedule.kind) {
		case 'off':
			return null;
		case 'daily':
			return `${schedule.minute} ${schedule.hour} * * *`;
		case 'weekly':
			return `${schedule.minute} ${schedule.hour} * * ${schedule.dayOfWeek}`;
	}
}

/** `HH:MM` (24-hour, what a time input gives) as hour and minute, or null. */
export function parseTimeOfDay(
	value: string,
): { hour: number; minute: number } | null {
	const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
	if (match == null || match[1] == null || match[2] == null) {
		return null;
	}
	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (hour > 23 || minute > 59) {
		return null;
	}
	return { hour, minute };
}

/** Hour and minute as `HH:MM`, the value a time input expects. */
export function formatTimeOfDay(hour: number, minute: number): string {
	return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Which app a field applies to and what it limits, for error messages. */
const SIGN_IN_SESSION_FIELD_LABELS: Readonly<
	Record<keyof SignInSessionPolicyModel, string>
> = {
	tokenExpiresIn: 'CRM: require signing in again',
	tokenExpiresInPublic: 'Ecommerce storefront: require signing in again',
	tokenExpirationSchedule: 'CRM: sign-out schedule',
	tokenExpirationSchedulePublic: 'Ecommerce storefront: sign-out schedule',
};

const DURATION_FIELDS: ReadonlyArray<keyof SignInSessionPolicyModel> = [
	'tokenExpiresIn',
	'tokenExpiresInPublic',
];

const SCHEDULE_FIELDS: ReadonlyArray<keyof SignInSessionPolicyModel> = [
	'tokenExpirationSchedule',
	'tokenExpirationSchedulePublic',
];

export interface SignInSessionUpdateResultModel {
	/** The fields to write; a field absent here is left unchanged. */
	update: UpdateSignInSessionSettingsRequestDto;
	/** Why the change cannot be saved, one line per field; empty when it can. */
	errors: Array<string>;
}

/**
 * Validate a "Sign-in sessions" change as it arrives over the network.
 *
 * Only the four policy fields are read; anything else in the body is
 * ignored. A field that is absent stays unchanged, null or blank turns it
 * off, and text must be what the CRM writes: a canonical duration (see
 * `canonicalTokenDurationMinutes`) or a daily or weekly schedule. A custom
 * schedule can be turned off but not saved, which also keeps a schedule that
 * fires every few minutes (and so signs everyone out constantly) from being
 * set through the CRM.
 *
 * crm-api also parses each schedule with cron-parser before saving.
 */
export function normalizeSignInSessionUpdate(
	body: unknown,
): SignInSessionUpdateResultModel {
	const update: UpdateSignInSessionSettingsRequestDto = {};
	const errors: Array<string> = [];
	if (body == null || typeof body !== 'object') {
		return { update, errors: ['The request body must be an object.'] };
	}
	const source = body as Partial<Record<keyof SignInSessionPolicyModel, unknown>>;

	const readField = (
		field: keyof SignInSessionPolicyModel,
	): string | null | undefined => {
		const value = source[field];
		if (value === undefined) {
			return undefined;
		}
		if (value === null) {
			return null;
		}
		if (typeof value !== 'string') {
			errors.push(
				`${SIGN_IN_SESSION_FIELD_LABELS[field]} must be text, or null to turn it off.`,
			);
			return undefined;
		}
		const trimmed = value.trim();
		if (trimmed === '') {
			return null;
		}
		if (trimmed.length > SIGN_IN_SESSION_COLUMN_MAX_LENGTH) {
			errors.push(
				`${SIGN_IN_SESSION_FIELD_LABELS[field]} is longer than ${SIGN_IN_SESSION_COLUMN_MAX_LENGTH} characters.`,
			);
			return undefined;
		}
		return trimmed;
	};

	for (const field of DURATION_FIELDS) {
		const value = readField(field);
		if (value === undefined) {
			continue;
		}
		if (value != null && canonicalTokenDurationMinutes(value) == null) {
			errors.push(
				`${SIGN_IN_SESSION_FIELD_LABELS[field]} must be a whole number of minutes, hours or days (for example 45m, 12h or 30d), from 1 minute to 365 days.`,
			);
			continue;
		}
		update[field] = value;
	}

	for (const field of SCHEDULE_FIELDS) {
		const value = readField(field);
		if (value === undefined) {
			continue;
		}
		if (value != null) {
			const schedule = cronToSchedule(value);
			if (schedule.kind !== 'daily' && schedule.kind !== 'weekly') {
				errors.push(
					`${SIGN_IN_SESSION_FIELD_LABELS[field]} must be a daily time (for example 0 2 * * *) or a weekly one (for example 0 2 * * 0).`,
				);
				continue;
			}
		}
		update[field] = value;
	}

	return { update, errors };
}
